/**
 * Zenith Pulse Continue Training — full fitness platform
 */
(function (global) {
  'use strict';

  const STORAGE_VERSION = 4;
  const MAX_WORKOUTS_PER_DAY = 8;
  const MAX_XP_WORKOUTS_PER_DAY = 3;
  const MAX_PHOTOS_XP_PER_DAY = 1;
  const MAX_WEEKLY_XP = 1200;
  const XP_PER_CREDIT = 150;
  const CREDIT_CENTS = 500;
  const MIN_WORKOUT_MIN = 10;
  const MAX_WORKOUT_MIN = 360;
  const MAX_WEIGHT_KG = 500;
  const MAX_REPS = 100;

  const XP_RULES = {
    account_create: { xp: 50, once: 'account_create' },
    complete_profile: { xp: 50, once: 'complete_profile' },
    first_workout: { xp: 100, once: 'first_workout' },
    log_workout: { xp: 10, dailyKey: 'workout_xp_count', dailyMax: 3 },
    workout_60min: { xp: 20, perWorkout: true },
    streak_7: { xp: 100, once: 'streak_7' },
    streak_30: { xp: 500, once: 'streak_30' },
    progress_photo: { xp: 25, dailyKey: 'photo_xp', dailyMax: 1 },
    product_review: { xp: 25, once: 'product_review' },
    purchase_100: { xp: 100, once: 'purchase_100' },
    purchase_200: { xp: 250, once: 'purchase_200' },
    referral: { xp: 250, oncePer: 'referral_codes' },
    challenge: { xp: 0, dynamic: true },
  };

  const LEVELS = [
    { key: 'bronze', name: 'Bronze Warrior', min: 0 },
    { key: 'silver', name: 'Silver Warrior', min: 200 },
    { key: 'gold', name: 'Gold Warrior', min: 500 },
    { key: 'elite', name: 'Elite Warrior', min: 1000 },
    { key: 'zenith', name: 'Zenith Athlete', min: 2000 },
    { key: 'apex', name: 'Apex Legend', min: 4000 },
  ];

  const CHALLENGES = [
    { id: 'discipline-30', name: '30 Day Discipline', desc: '20 training days in 30 days', target: 20, xp: 250 },
    { id: 'run-100', name: '100km Running', desc: 'Log 100km running', target: 100, metric: 'run_km', xp: 400 },
    { id: 'hyrox-4', name: 'HYROX Beginner', desc: '4 HYROX sessions', target: 4, metric: 'hyrox', xp: 350 },
    { id: 'push-1k', name: 'Push Up 1000', desc: '1000 push-up reps logged', target: 1000, metric: 'pushups', xp: 300 },
    { id: 'strength-12', name: 'Strength 12', desc: '12 gym sessions', target: 12, metric: 'gym', xp: 350 },
    { id: 'apex-month', name: 'Apex Monthly', desc: '18 workouts in 30 days', target: 18, xp: 1000 },
  ];

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function dayKey(d) {
    return (d || new Date()).toISOString().slice(0, 10);
  }

  function weekKey() {
    const d = new Date();
    const onejan = new Date(d.getFullYear(), 0, 1);
    const w = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
    return d.getFullYear() + '-W' + w;
  }

  function storageKey() {
    const root = document.getElementById('zenith-training-app');
    return 'zp_training_v' + STORAGE_VERSION + '_' + (root?.dataset?.customerId || 'guest');
  }

  function fingerprint(obj) {
    const s = JSON.stringify(obj);
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
    return 'fp_' + Math.abs(h).toString(36);
  }

  function defaultState() {
    return {
      v: STORAGE_VERSION,
      profile: {
        name: '',
        age: '',
        heightCm: '',
        weightKg: '',
        trainingDaysPerWeek: 4,
        goals: [],
        complete: false,
      },
      workouts: [],
      measurements: [],
      progressPhotos: [],
      personalRecords: [],
      challenges: {},
      streak: { current: 0, lastDay: '' },
      activityXp: 0,
      creditsRedeemedCents: 0,
      xpAudit: [],
      onceFlags: {},
      dayLimits: { key: dayKey(), workoutLogs: 0, workoutXp: 0, photoXp: 0 },
      weekXp: { key: weekKey(), earned: 0 },
      workoutFingerprints: [],
      referralCodes: [],
      createdAt: new Date().toISOString(),
    };
  }

  function loadState() {
    try {
      const raw = localStorage.getItem(storageKey());
      if (!raw) return defaultState();
      const s = JSON.parse(raw);
      if (s.v !== STORAGE_VERSION) return migrateState(s);
      if (s.dayLimits?.key !== dayKey()) {
        s.dayLimits = { key: dayKey(), workoutLogs: 0, workoutXp: 0, photoXp: 0 };
      }
      if (s.weekXp?.key !== weekKey()) s.weekXp = { key: weekKey(), earned: 0 };
      return s;
    } catch (e) {
      return defaultState();
    }
  }

  function migrateState(old) {
    const s = defaultState();
    s.profile = { ...s.profile, ...(old.profile || {}) };
    s.workouts = old.workouts || [];
    s.measurements = old.measurements || [];
    s.activityXp = old.activityXp || 0;
    s.xpAudit = old.xpLedger || old.xpAudit || [];
    s.onceFlags = old.onceFlags || {};
    return s;
  }

  function saveState(s) {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(s));
    } catch (e) {}
  }

  function getRoot() {
    return document.getElementById('zenith-training-app');
  }

  function shopXp() {
    return parseInt(getRoot()?.dataset?.shopXp || '0', 10) || 0;
  }

  function shopSpentCents() {
    return parseInt(getRoot()?.dataset?.shopSpentCents || '0', 10) || 0;
  }

  function totalXp(s) {
    return shopXp() + (s.activityXp || 0);
  }

  function levelForXp(xp) {
    let cur = LEVELS[0];
    for (let i = LEVELS.length - 1; i >= 0; i--) {
      if (xp >= LEVELS[i].min) {
        cur = LEVELS[i];
        break;
      }
    }
    const idx = LEVELS.indexOf(cur);
    const next = LEVELS[idx + 1];
    const pct = next ? Math.min(100, Math.round(((xp - cur.min) / (next.min - cur.min)) * 100)) : 100;
    return { current: cur, next, progress: pct, xpToNext: next ? next.min - xp : 0 };
  }

  function creditsCents(s) {
    const blocks = Math.floor(totalXp(s) / XP_PER_CREDIT);
    const total = blocks * CREDIT_CENTS;
    return Math.max(0, total - (s.creditsRedeemedCents || 0));
  }

  function auditXp(s, type, xp, meta, status) {
    const entry = {
      id: 'xp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      type,
      xp,
      at: new Date().toISOString(),
      status: status || 'approved',
      fingerprint: meta?.fingerprint || '',
      meta: meta || {},
    };
    s.xpAudit.unshift(entry);
    if (s.xpAudit.length > 500) s.xpAudit.length = 500;
    return entry;
  }

  function awardXp(s, type, amount, meta) {
    if (amount <= 0) return { ok: false, reason: 'Invalid XP' };
    if (s.weekXp.earned + amount > MAX_WEEKLY_XP) {
      auditXp(s, type, amount, meta, 'rejected_weekly_cap');
      return { ok: false, reason: 'Weekly XP cap reached (' + MAX_WEEKLY_XP + ').' };
    }

    const rule = XP_RULES[type];
    if (rule?.once && s.onceFlags[rule.once]) {
      auditXp(s, type, amount, meta, 'rejected_duplicate');
      return { ok: false, reason: 'Already claimed.' };
    }
    if (rule?.dailyKey) {
      const key = rule.dailyKey === 'workout_xp_count' ? 'workoutXp' : 'photoXp';
      if ((s.dayLimits[key] || 0) >= rule.dailyMax) {
        auditXp(s, type, amount, meta, 'rejected_daily_cap');
        return { ok: false, reason: 'Daily XP limit for this action.' };
      }
      s.dayLimits[key] = (s.dayLimits[key] || 0) + 1;
    }

    if (meta?.fingerprint && s.xpAudit.some((a) => a.fingerprint === meta.fingerprint && a.status === 'approved')) {
      auditXp(s, type, amount, meta, 'rejected_duplicate_fp');
      return { ok: false, reason: 'Duplicate XP claim blocked.' };
    }

    s.activityXp += amount;
    s.weekXp.earned += amount;
    if (rule?.once) s.onceFlags[rule.once] = true;
    auditXp(s, type, amount, meta, 'approved');
    saveState(s);
    return { ok: true, xp: amount };
  }

  function validateWorkout(payload) {
    const duration = parseInt(payload.durationMinutes, 10) || 0;
    if (duration < MIN_WORKOUT_MIN) return { ok: false, reason: 'Minimum ' + MIN_WORKOUT_MIN + ' minutes.' };
    if (duration > MAX_WORKOUT_MIN) return { ok: false, reason: 'Duration exceeds realistic limit.' };
    for (const ex of payload.exercises || []) {
      const w = parseFloat(String(ex.weight).replace(/[^\d.]/g, '')) || 0;
      const r = parseInt(ex.reps, 10) || 0;
      if (w > MAX_WEIGHT_KG) return { ok: false, reason: 'Impossible weight entry blocked.' };
      if (r > MAX_REPS) return { ok: false, reason: 'Impossible rep count blocked.' };
    }
    return { ok: true };
  }

  function validateWorkoutAntiCheat(s, w, fp) {
    if (s.dayLimits.workoutLogs >= MAX_WORKOUTS_PER_DAY) {
      return { ok: false, reason: 'Maximum ' + MAX_WORKOUTS_PER_DAY + ' workouts per day.' };
    }
    if (s.workoutFingerprints.includes(fp)) {
      return { ok: false, reason: 'Duplicate workout submission blocked.' };
    }
    const sameDay = s.workouts.filter((x) => x.date === w.date && x.name === w.name && x.type === w.type);
    if (sameDay.length >= 3) {
      return { ok: false, reason: 'Too many similar workouts today — anti-spam.' };
    }
    return { ok: true };
  }

  function updateStreak(s, date) {
    const last = s.streak.lastDay;
    if (last === date) return;
    const y = new Date(date);
    y.setDate(y.getDate() - 1);
    const yStr = y.toISOString().slice(0, 10);
    if (last === yStr) s.streak.current += 1;
    else s.streak.current = 1;
    s.streak.lastDay = date;
    if (s.streak.current >= 7 && !s.onceFlags.streak_7) awardXp(s, 'streak_7', 100, {});
    if (s.streak.current >= 30 && !s.onceFlags.streak_30) awardXp(s, 'streak_30', 500, {});
  }

  function logWorkout(s, payload) {
    const val = validateWorkout(payload);
    if (!val.ok) return val;

    const w = {
      id: 'w_' + Date.now(),
      name: String(payload.name || 'Workout').trim(),
      type: payload.type || 'gym',
      location: payload.location || '',
      date: payload.date || dayKey(),
      durationMinutes: parseInt(payload.durationMinutes, 10),
      notes: payload.notes || '',
      exercises: (payload.exercises || []).map((ex) => ({
        name: ex.name,
        weight: ex.weight,
        sets: ex.sets,
        reps: ex.reps,
        rpe: ex.rpe,
        distance: ex.distance,
        time: ex.time,
        notes: ex.notes,
      })),
      createdAt: new Date().toISOString(),
    };

    const fp = fingerprint({ n: w.name, t: w.type, d: w.date, e: w.exercises });
    const ac = validateWorkoutAntiCheat(s, w, fp);
    if (!ac.ok) return ac;

    s.workouts.unshift(w);
    if (s.workouts.length > 300) s.workouts.length = 300;
    s.workoutFingerprints.push(fp);
    if (s.workoutFingerprints.length > 400) s.workoutFingerprints.shift();
    s.dayLimits.workoutLogs += 1;

    updateStreak(s, w.date);
    updatePRs(s, w);
    updateChallenges(s, w);

    const xpResults = [];
    if (!s.onceFlags.first_workout) {
      xpResults.push(awardXp(s, 'first_workout', 100, { fingerprint: fp + '_first' }));
    }
    xpResults.push(awardXp(s, 'log_workout', 10, { fingerprint: fp + '_log', workoutId: w.id }));
    if (w.durationMinutes >= 60) {
      xpResults.push(awardXp(s, 'workout_60min', 20, { fingerprint: fp + '_60', workoutId: w.id }));
    }

    saveState(s);
    return { ok: true, workout: w, xpResults };
  }

  function updatePRs(s, w) {
    (w.exercises || []).forEach((ex) => {
      const weight = parseFloat(String(ex.weight).replace(/[^\d.]/g, '')) || 0;
      const reps = parseInt(ex.reps, 10) || 0;
      if (!ex.name || weight <= 0) return;
      const AI = global.ZenithTrainingAI;
      const score = AI ? AI.e1rm(weight, reps) : weight * reps;
      const pr = s.personalRecords.find((p) => AI && AI.normName(p.name) === AI.normName(ex.name));
      if (!pr || score > pr.score) {
        if (pr) Object.assign(pr, { weight, reps, score, date: w.date, name: ex.name });
        else s.personalRecords.push({ name: ex.name, weight, reps, score, date: w.date });
      }
    });
  }

  function updateChallenges(s, w) {
    CHALLENGES.forEach((ch) => {
      if (!s.challenges[ch.id]) s.challenges[ch.id] = { progress: 0, completed: false };
      const c = s.challenges[ch.id];
      if (c.completed) return;
      if (!ch.metric) c.progress += 1;
      if (ch.metric === 'gym' && w.type === 'gym') c.progress += 1;
      if (ch.metric === 'hyrox' && w.type === 'hyrox') c.progress += 1;
      if (ch.metric === 'run_km' && w.type === 'running') {
        w.exercises.forEach((ex) => {
          c.progress += parseFloat(ex.distance) || 0;
        });
      }
      if (ch.metric === 'pushups') {
        w.exercises.forEach((ex) => {
          if (/push/i.test(ex.name)) c.progress += parseInt(ex.reps, 10) || 0;
        });
      }
      if (c.progress >= ch.target) {
        c.completed = true;
        awardXp(s, 'challenge', ch.xp, { challengeId: ch.id, fingerprint: 'ch_' + ch.id });
      }
    });
  }

  function profileComplete(s) {
    const p = s.profile;
    return !!(p.name && p.age && p.heightCm && p.weightKg && (p.goals || []).length);
  }

  function syncShopRewards(s) {
    const cents = shopSpentCents();
    if (cents >= 20000 && !s.onceFlags.purchase_200) awardXp(s, 'purchase_200', 250, {});
    else if (cents >= 10000 && !s.onceFlags.purchase_100) awardXp(s, 'purchase_100', 100, {});
  }

  function drawChart(container, data, key, label) {
    if (!container) return;
    const points = data.filter((d) => d[key] != null).reverse();
    if (points.length < 2) {
      container.innerHTML = '<p class="zt-muted">Need 2+ entries for ' + label + ' chart.</p>';
      return;
    }
    const vals = points.map((p) => p[key]);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = 40;
    const w = container.clientWidth || 320;
    const h = 160;
    const range = max - min || 1;
    const coords = vals.map((v, i) => {
      const x = pad + (i / (vals.length - 1)) * (w - pad * 2);
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return x + ',' + y;
    });
    container.innerHTML =
      '<svg viewBox="0 0 ' +
      w +
      ' ' +
      h +
      '" class="zt-chart-svg" role="img" aria-label="' +
      label +
      ' chart"><polyline fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="2" points="' +
      coords.join(' ') +
      '"/></svg><p class="zt-muted">' +
      label +
      ': ' +
      vals[vals.length - 1] +
      ' → ' +
      vals[0] +
      ' (' +
      points.length +
      ' points)</p>';
  }

  function applyCredits(s) {
    const cents = creditsCents(s);
    if (cents < CREDIT_CENTS) return Promise.reject(new Error('Need 150+ XP ($5 AUD minimum).'));
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          zenith_credits_aud: (cents / 100).toFixed(2),
          zenith_credits_cents: String(cents),
          zenith_customer: getRoot()?.dataset?.customerId,
        },
      }),
    }).then((r) => {
      if (!r.ok) throw new Error('Cart update failed');
      s.creditsRedeemedCents = (s.creditsRedeemedCents || 0) + cents;
      saveState(s);
      window.location.href = '/cart';
    });
  }

  function initTrainingApp() {
    const root = getRoot();
    if (!root) return;

    let state = loadState();
    const AI = global.ZenithTrainingAI;
    if (!AI) {
      console.error('ZenithTrainingAI not loaded');
      return;
    }

    if (root.dataset.loggedIn === 'true') {
      if (!state.onceFlags.account_create) awardXp(state, 'account_create', 50, {});
      syncShopRewards(state);
      state = loadState();
    }

    function setNav(section) {
      root.querySelectorAll('[data-zt-nav]').forEach((n) => n.classList.toggle('is-active', n.dataset.ztNav === section));
      root.querySelectorAll('[data-zt-section]').forEach((sec) => sec.classList.toggle('is-active', sec.dataset.ztSection === section));
    }

    function renderGlobal() {
      const xp = totalXp(state);
      const lvl = levelForXp(xp);
      const cred = (creditsCents(state) / 100).toFixed(2);
      root.querySelectorAll('[data-zt-xp]').forEach((el) => (el.textContent = xp));
      root.querySelectorAll('[data-zt-level]').forEach((el) => {
        el.textContent = lvl.current.name;
        el.className = 'zt-level-badge zf-level--' + lvl.current.key;
      });
      root.querySelectorAll('[data-zt-credits]').forEach((el) => (el.textContent = '$' + cred + ' AUD'));
      root.querySelectorAll('[data-zt-streak]').forEach((el) => (el.textContent = state.streak.current));
      const prog = root.querySelector('[data-zt-progress]');
      if (prog) prog.style.setProperty('--zf-progress', lvl.progress + '%');
    }

    function renderAICoach() {
      const report = AI.generateFullReport(state);
      const el = root.querySelector('[data-zt-ai-report]');
      if (!el) return;
      let html = '<p class="zt-ai-headline">' + report.headline + '</p>';
      if (report.deload) html += '<div class="zt-alert">' + report.deload.message + '</div>';
      if (report.exerciseInsights.length) {
        html += '<h3>Exercise analysis</h3><ul class="zt-insights">';
        report.exerciseInsights.forEach((i) => {
          html += '<li><strong>' + i.exercise + '</strong>: ' + i.message + '</li>';
        });
        html += '</ul>';
      }
      ['muscleGain', 'weightLoss', 'hyrox', 'strength', 'recovery'].forEach((k) => {
        if (report[k]?.length) {
          html += '<h3>' + k + '</h3><ul>' + report[k].map((x) => '<li>' + x + '</li>').join('') + '</ul>';
        }
      });
      html += '<h3>Weekly plan</h3><ol>' + report.weeklyPlan.map((d) => '<li>' + d + '</li>').join('') + '</ol>';
      el.innerHTML = html;
    }

    function renderToday() {
      const plan = AI.generateDailyPlan(state);
      const box = root.querySelector('[data-zt-today-plan]');
      if (!box) return;
      box.innerHTML =
        '<h3>' +
        plan.focus +
        '</h3><ul>' +
        plan.exercises.map((e) => '<li>' + e + '</li>').join('') +
        '</ul><button type="button" class="zp-btn zp-btn--primary" data-zt-start-log>Log this session</button>';
      box.querySelector('[data-zt-start-log]')?.addEventListener('click', () => setNav('history'));
    }

    function renderHistory() {
      const list = root.querySelector('[data-zt-history-list]');
      if (!list) return;
      if (!state.workouts.length) {
        list.innerHTML = '<p class="zt-muted">No workouts yet.</p>';
        return;
      }
      list.innerHTML = state.workouts
        .slice(0, 50)
        .map((w) => {
          const ex = (w.exercises || [])
            .map(
              (e) =>
                '<li>' +
                e.name +
                ' — ' +
                (e.weight ? e.weight + 'kg ' : '') +
                (e.sets ? e.sets + '×' : '') +
                (e.reps || '') +
                (e.rpe ? ' @RPE' + e.rpe : '') +
                (e.notes ? ' (' + e.notes + ')' : '') +
                '</li>'
            )
            .join('');
          return (
            '<article class="zt-workout-card"><header><strong>' +
            w.name +
            '</strong> <span class="zt-muted">' +
            w.type +
            ' · ' +
            w.date +
            ' · ' +
            w.durationMinutes +
            'min</span></header><ul>' +
            ex +
            '</ul><p class="zt-muted">' +
            (w.location || '') +
            ' ' +
            (w.notes || '') +
            '</p></article>'
          );
        })
        .join('');
    }

    function renderProgress() {
      const range = root.querySelector('[data-zt-range]:checked')?.value || '90d';
      const days = { week: 7, month: 30, '6m': 183, year: 365, all: 99999 }[range] || 90;
      const data = state.measurements.filter((m) => (Date.now() - new Date(m.date)) / 86400000 <= days);
      drawChart(root.querySelector('[data-zt-chart-weight]'), data, 'weight', 'Weight');
      drawChart(root.querySelector('[data-zt-chart-waist]'), data, 'waist', 'Waist');
      const photos = root.querySelector('[data-zt-photo-grid]');
      if (photos) {
        photos.innerHTML = state.progressPhotos
          .slice(0, 12)
          .map((p) => '<img src="' + p.dataUrl + '" alt="Progress ' + p.date + '" loading="lazy">')
          .join('') || '<p class="zt-muted">No photos yet.</p>';
      }
    }

    function renderChallenges() {
      const grid = root.querySelector('[data-zt-challenges]');
      if (!grid) return;
      grid.innerHTML = CHALLENGES.map((ch) => {
        const c = state.challenges[ch.id] || { progress: 0, completed: false };
        const pct = Math.min(100, Math.round((c.progress / ch.target) * 100));
        return (
          '<article class="zt-ch-card' +
          (c.completed ? ' is-done' : '') +
          '"><h4>' +
          ch.name +
          '</h4><p>' +
          ch.desc +
          '</p><div class="zf-progress"><div class="zf-progress__fill" style="--zf-progress:' +
          pct +
          '%"></div></div><p>' +
          Math.min(c.progress, ch.target) +
          '/' +
          ch.target +
          ' · ' +
          ch.xp +
          ' XP</p></article>'
        );
      }).join('');
    }

    function renderRewards() {
      const audit = root.querySelector('[data-zt-audit]');
      if (audit) {
        audit.innerHTML = state.xpAudit
          .slice(0, 30)
          .map(
            (a) =>
              '<li class="zt-audit-' +
              a.status +
              '"><span>' +
              a.at.slice(0, 10) +
              '</span> ' +
              a.type +
              ' <strong>+' +
              a.xp +
              '</strong> <em>' +
              a.status +
              '</em></li>'
          )
          .join('');
      }
      const tiers = root.querySelector('[data-zt-credit-tiers]');
      if (tiers) {
        const xp = totalXp(state);
        tiers.innerHTML = [
          { xp: 150, aud: 5 },
          { xp: 300, aud: 10 },
          { xp: 600, aud: 20 },
          { xp: 1500, aud: 50 },
        ]
          .map((t) => '<li>' + t.xp + ' XP = $' + t.aud + ' AUD ' + (xp >= t.xp ? '✓' : '') + '</li>')
          .join('');
      }
    }

    function renderAchievements() {
      const grid = root.querySelector('[data-zt-achievements]');
      if (!grid) return;
      const xp = totalXp(state);
      const items = [
        ['First Workout', state.workouts.length >= 1],
        ['7-Day Streak', state.streak.current >= 7],
        ['30-Day Streak', state.streak.current >= 30],
        ['Profile Complete', profileComplete(state)],
        ['Silver Warrior', xp >= 200],
        ['Apex Legend', xp >= 4000],
        ['Challenge Champion', Object.values(state.challenges).some((c) => c.completed)],
      ];
      grid.innerHTML = items
        .map(([n, ok]) => '<li class="zf-achievement' + (ok ? ' is-earned' : '') + '">◆ ' + n + '</li>')
        .join('');
    }

    function renderAll() {
      renderGlobal();
      renderAICoach();
      renderToday();
      renderHistory();
      renderProgress();
      renderChallenges();
      renderRewards();
      renderAchievements();
    }

    root.querySelectorAll('[data-zt-nav]').forEach((btn) => {
      btn.addEventListener('click', () => setNav(btn.dataset.ztNav));
    });

    const logForm = root.querySelector('#zt-workout-form');
    if (logForm) {
      const exBox = logForm.querySelector('[data-zt-exercises]');
      const addRow = () => {
        const row = document.createElement('div');
        row.className = 'zt-ex-row';
        row.innerHTML =
          '<input name="ex_name[]" placeholder="Exercise name" required>' +
          '<input name="ex_weight[]" placeholder="Weight kg">' +
          '<input name="ex_sets[]" placeholder="Sets" type="number">' +
          '<input name="ex_reps[]" placeholder="Reps" type="number">' +
          '<input name="ex_rpe[]" placeholder="RPE" type="number" min="1" max="10" step="0.5">' +
          '<input name="ex_notes[]" placeholder="Notes">';
        exBox.appendChild(row);
      };
      logForm.querySelector('[data-zt-add-ex]')?.addEventListener('click', addRow);
      addRow();
      logForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const fd = new FormData(logForm);
        const names = fd.getAll('ex_name[]');
        const exercises = names.map((name, i) => ({
          name,
          weight: fd.getAll('ex_weight[]')[i],
          sets: fd.getAll('ex_sets[]')[i],
          reps: fd.getAll('ex_reps[]')[i],
          rpe: fd.getAll('ex_rpe[]')[i],
          notes: fd.getAll('ex_notes[]')[i],
        }));
        const res = logWorkout(state, {
          name: fd.get('name'),
          type: fd.get('type'),
          location: fd.get('location'),
          date: fd.get('date'),
          durationMinutes: fd.get('duration'),
          notes: fd.get('notes'),
          exercises,
        });
        const msg = root.querySelector('[data-zt-log-msg]');
        if (msg) {
          msg.textContent = res.ok
            ? 'Saved. ' + (res.xpResults || []).filter((x) => x.ok).map((x) => '+' + x.xp + ' XP').join(' ')
            : res.reason;
          msg.className = 'zt-form-msg ' + (res.ok ? 'is-ok' : 'is-err');
        }
        if (res.ok) {
          logForm.reset();
          exBox.innerHTML = '';
          addRow();
          state = loadState();
          renderAll();
        }
      });
    }

    root.querySelector('#zt-measure-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(e.target);
      state.measurements.unshift({
        date: fd.get('date') || dayKey(),
        weight: parseFloat(fd.get('weight')) || null,
        bodyFat: parseFloat(fd.get('bodyFat')) || null,
        chest: parseFloat(fd.get('chest')) || null,
        waist: parseFloat(fd.get('waist')) || null,
        arms: parseFloat(fd.get('arms')) || null,
        legs: parseFloat(fd.get('legs')) || null,
        shoulders: parseFloat(fd.get('shoulders')) || null,
      });
      saveState(state);
      renderProgress();
      e.target.reset();
    });

    root.querySelector('#zt-photo-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const file = e.target.querySelector('input[type=file]').files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const fp = fingerprint({ photo: file.name, d: dayKey() });
        state.progressPhotos.unshift({ date: dayKey(), dataUrl: reader.result });
        const xp = awardXp(state, 'progress_photo', 25, { fingerprint: fp });
        saveState(state);
        state = loadState();
        root.querySelector('[data-zt-photo-msg]').textContent = xp.ok ? '+25 XP' : xp.reason;
        renderProgress();
      };
      reader.readAsDataURL(file);
    });

    root.querySelector('#zt-profile-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(e.target);
      state.profile = {
        name: fd.get('name'),
        age: fd.get('age'),
        heightCm: fd.get('height'),
        weightKg: fd.get('weight'),
        trainingDaysPerWeek: parseInt(fd.get('training_days'), 10) || 4,
        goals: fd.getAll('goals'),
        complete: false,
      };
      state.profile.complete = profileComplete(state);
      if (state.profile.complete && !state.onceFlags.complete_profile) {
        awardXp(state, 'complete_profile', 50, {});
      }
      saveState(state);
      state = loadState();
      renderAll();
    });

    root.querySelector('#zt-size-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(e.target);
      const result = AI.recommendSize({
        height: fd.get('height'),
        weight: fd.get('weight'),
        chest: fd.get('chest'),
        waist: fd.get('waist'),
        shoulders: fd.get('shoulders'),
        build: fd.get('build'),
        fitPreference: fd.get('fit'),
      });
      const out = root.querySelector('[data-zt-size-result]');
      if (out) {
        out.hidden = false;
        out.innerHTML =
          '<p class="zt-size-big">' +
          result.size +
          '</p><p>Confidence: <strong>' +
          result.confidence +
          '%</strong></p><p>' +
          result.reason +
          '</p>';
      }
    });

    root.querySelector('[data-zt-apply-credits]')?.addEventListener('click', () => applyCredits(state).catch((err) => alert(err.message)));

    root.querySelectorAll('[data-zt-range]').forEach((r) => r.addEventListener('change', renderProgress));

    root.querySelector('[data-zt-refresh-ai]')?.addEventListener('click', renderAICoach);

    renderAll();
  }

  function injectProductSizeButton() {
    if (document.querySelector('[data-zp-size-open]') || document.getElementById('zenith-size-calculator')) return;
    if (document.body.dataset.template !== 'product' && !document.querySelector('[data-template="product"]')) {
      const main = document.getElementById('MainContent');
      if (!main || !/\/products\//.test(location.pathname)) return;
    }
    const target =
      document.querySelector('.product-form-buttons') ||
      document.querySelector('[data-add-to-cart]')?.closest('form') ||
      document.querySelector('.buy-buttons-block') ||
      document.querySelector('#MainContent .product-information');
    if (!target) return;
    const wrap = document.createElement('div');
    wrap.className = 'zp-size-calc';
    wrap.style.marginTop = '1rem';
    wrap.innerHTML =
      '<button type="button" class="zp-btn zp-btn--ghost" data-zp-size-open>Find My Perfect Size (AI)</button>';
    target.appendChild(wrap);
  }

  function initProductSizeCalculator() {
    const AI = global.ZenithTrainingAI;
    const modal = document.getElementById('zenith-size-modal');
    const form = document.getElementById('zp-size-form');
    injectProductSizeButton();
    if (!modal || !form || !AI) return;

    document.querySelectorAll('[data-zp-size-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        modal.hidden = false;
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('zp-modal-open');
      });
    });
    document.querySelectorAll('[data-zp-size-close]').forEach((btn) => {
      btn.addEventListener('click', () => {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('zp-modal-open');
      });
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const fitMap = { athletic: 'regular', compression: 'compression', relaxed: 'oversized' };
      const result = AI.recommendSize({
        height: fd.get('height'),
        weight: fd.get('weight'),
        chest: fd.get('chest'),
        waist: fd.get('waist'),
        shoulders: fd.get('shoulders') || fd.get('chest'),
        build: fd.get('build') || fd.get('bodyType'),
        fitPreference: fitMap[fd.get('desired_fit')] || fd.get('desired_fit') || 'regular',
      });
      const resultBox = document.getElementById('zp-size-result');
      const out = document.querySelector('[data-zp-size-output]');
      const letter = document.querySelector('[data-zp-size-letter]');
      const conf = document.querySelector('[data-zp-confidence]');
      const fitNote = document.querySelector('[data-zp-fit-note]');
      if (out) out.textContent = result.size + ' (AU)';
      if (letter) letter.textContent = result.letter;
      if (conf) conf.textContent = result.confidence + '%';
      if (fitNote) fitNote.textContent = result.reason;
      if (resultBox) resultBox.hidden = false;
      form.hidden = true;
    });

    document.querySelector('[data-zp-size-recalc]')?.addEventListener('click', () => {
      form.hidden = false;
      document.getElementById('zp-size-result').hidden = true;
    });
  }

  function initMotivation() {
    const el = document.getElementById('zenith-motivation');
    if (!el) return;
    const card = el.querySelector('[data-zp-motivation-text]');
    let quotes = ['Discipline Beats Motivation.', 'Earn XP. Earn Respect.'];
    try {
      const j = document.getElementById('zenith-motivation-quotes');
      if (j) quotes = JSON.parse(j.textContent);
    } catch (e) {}
    let dismissed = false;
    el.querySelector('[data-zp-motivation-close]')?.addEventListener('click', () => {
      dismissed = true;
      el.classList.remove('is-visible');
    });
    function show() {
      if (dismissed || !card) return;
      card.textContent = quotes[Math.floor(Math.random() * quotes.length)];
      el.classList.add('is-visible');
      setTimeout(() => el.classList.remove('is-visible'), 7000);
    }
    setTimeout(show, 200000);
    setInterval(show, 240000);
  }

  global.ZenithTraining = { loadState, saveState, logWorkout, totalXp, awardXp };

  function scrollToTrainingHub() {
    if (location.hash !== '#zenith-training-hub') return;
    const hub = document.getElementById('zenith-training-hub');
    if (hub) {
      setTimeout(() => hub.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }

  if (!global.__zenithTrainingBooted) {
    global.__zenithTrainingBooted = true;
    ready(() => {
      initTrainingApp();
      initProductSizeCalculator();
      initMotivation();
      scrollToTrainingHub();
    });
    window.addEventListener('hashchange', scrollToTrainingHub);
  }
})(window);
